import { escapeHtml, safeUrl } from '../ui.js';
import { driveThumbnail, downloadDriveFile } from '../google-api.js';
import { toDate, mediaKind, dayFolder, recordedLine, maskPhone, looksLikePhone } from '../wa-core.js';

// THE WHATSAPP PIECES EVERY SCREEN DRAWS (17/10/2026): the channel marker, a
// message's photos and voice note, its meta line. The project timeline
// (wa-timeline.js), Mail's reader (wa-reader.js) and the import dialog all
// draw a message through these, so one message looks the same everywhere.
//
// Rendering safety (house rule 0): every value through escapeHtml, every
// data-built URL through safeUrl. Photos are Drive's own thumbnails (img-src
// https: is allowed); a voice note is fetched with the person's own Drive
// token and played through Web Audio — the app's CSP has no media-src, so an
// <audio src="blob:…"> would be refused, and Web Audio needs none.

/** Where the contract files photos: the project's Site folder, by date. One constant so the copy and the uploader agree. */
// Where WhatsApp photos are filed — ASP's project template, as the server files them
// (functions/whatsapp/drive.js): <project>/7. Images/Site/<YYYY-MM-DD>/.
export const PHOTO_PATH = ['7. Images', 'Site'];
export const SITE_FOLDER = PHOTO_PATH.join(' › ');

export const WA_SVG = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M14 8a6 6 0 0 1-8.9 5.2L2 14l.8-3A6 6 0 1 1 14 8z"></path></svg>';
export const FWD_SVG = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M3 8h7M7 4l4 4-4 4"></path><path d="M13 3v10"></path></svg>';
/** safeUrl, with '' (not '#') when the value is not an http(s) URL. */
export const httpUrl = (x) => { const v = safeUrl(x); return v === '#' ? '' : v; };
const PLAY_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"></path></svg>';
const PAUSE_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"></path></svg>';

/**
 * The channel marker under RECEIVED / SENT: the WhatsApp glyph (in --wa-green)
 * and a word. kind: 'whatsapp' | 'forwarded' | 'imported'. iconOnly for Mail's list.
 */
export function chMark(kind = 'whatsapp', { iconOnly = false, sent = false, text = '' } = {}) {
  const fwd = kind === 'forwarded';
  const word = text || (fwd ? 'Forwarded' : kind === 'imported' ? 'Imported' : 'WhatsApp');
  return `<span class="wa-ch${sent ? ' is-sent' : ''}${fwd ? ' is-fwd' : ''}"${iconOnly ? ` role="img" aria-label="${escapeHtml(word)}"` : ''}>${fwd ? FWD_SVG : WA_SVG}${iconOnly ? '' : escapeHtml(word)}</span>`;
}
/** The marker kind for a message: forwarded to Track, imported from a phone's export, or straight off the API / the phone app. */
export const markOf = (m) => (m && m.source === 'forward' ? 'forwarded' : m && m.source === 'import' ? 'imported' : 'whatsapp');

const pad = (n) => String(n).padStart(2, '0');
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Today 09:12" · "Yday 18:40" · "Fri 16 Oct" (this year) · "16 Oct 2025". */
export function whenLabel(v, now = new Date()) {
  const d = toDate(v);
  if (!d) return '';
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (diff === 0) return `Today ${hm}`;
  if (diff === 1) return `Yday ${hm}`;
  if (d.getFullYear() === now.getFullYear()) return `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
  return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()}`;
}
export const hm = (v) => { const d = toDate(v); return d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : ''; };

/** Who a message is from, as words: a name, else a masked number. */
export function senderName(m, thread = null) {
  if (m && m.direction === 'out') return (m.sentByName || (m.from && m.from.name) || 'ASP').trim();
  const confirmed = m && m.senderConfirmed && m.senderConfirmed.name;
  const n = confirmed || (m && m.from && m.from.name) || (thread && thread.contact && thread.contact.name) || (thread && thread.displayName) || '';
  if (n && !looksLikePhone(n)) return n;
  const wa = (m && m.from && m.from.waId) || (thread && thread.waId) || n;
  return wa ? maskPhone(`+${String(wa).replace(/^\+/, '')}`) : 'Unknown sender';
}

// ── Photos ─────────────────────────────────────────────────────────────────
/** Photo tiles for a list of image messages (or media objects). Each opens the file in Track's viewer. */
export function photosHtml(items, { small = false } = {}) {
  const media = items.map((x) => (x && x.media ? x.media : x)).filter((x) => x && (x.driveFileId || x.driveUrl));
  if (!media.length) return '';
  return `<div class="wa-photos${small ? ' is-small' : ''}">${media.map((md, i) => `<button type="button" class="wa-ph" data-wa-thumb="${escapeHtml(md.driveFileId || '')}" data-wa-file="${escapeHtml(md.driveFileId || '')}" data-wa-url="${escapeHtml(httpUrl(md.driveUrl))}" data-wa-name="${escapeHtml(md.filename || `Photo ${i + 1}`)}" data-wa-mime="${escapeHtml(md.mimeType || 'image/jpeg')}" aria-label="${escapeHtml(`Photo ${i + 1}${md.filename ? `, ${md.filename}` : ''}`)}"><span class="wa-ph-label">Photo</span></button>`).join('')}</div>`;
}
const thumbs = new Map();
/** Swap each grey tile for Drive's thumbnail once Drive answers; a tile Drive cannot thumbnail stays grey. */
export function hydrateThumbs(root) {
  root.querySelectorAll('[data-wa-thumb]:not([data-wa-done])').forEach((el) => {
    el.setAttribute('data-wa-done', '1');
    const id = el.getAttribute('data-wa-thumb');
    if (!id) return;
    const put = (url) => {
      if (!url || !el.isConnected) return;
      const img = new Image();
      img.alt = '';
      img.referrerPolicy = 'no-referrer';
      img.onload = () => { if (el.isConnected) { el.querySelector('.wa-ph-label')?.remove(); el.appendChild(img); el.classList.add('has-img'); } };
      img.src = safeUrl(String(url).replace(/=s\d+$/, '=s400'));
    };
    if (thumbs.has(id)) { thumbs.get(id).then(put, () => {}); return; }
    const p = driveThumbnail(id);
    thumbs.set(id, p);
    p.then(put).catch(() => {});
  });
}
/** A tile clicked: Track's own file viewer (lazy: google-panels is heavy). */
export function openPhoto(el) {
  const id = el.getAttribute('data-wa-file');
  const url = httpUrl(el.getAttribute('data-wa-url'));
  if (!id && !url) return;
  if (!id) { window.open(safeUrl(url), '_blank', 'noopener'); return; }
  import('./google-panels.js').then((m) => m.openFile({ id, name: el.getAttribute('data-wa-name') || 'Photo', mimeType: el.getAttribute('data-wa-mime') || 'image/jpeg', webViewLink: url || `https://drive.google.com/file/d/${encodeURIComponent(id)}/view` }))
    .catch(() => { if (url) window.open(safeUrl(url), '_blank', 'noopener'); });
}

// ── Voice notes ────────────────────────────────────────────────────────────
const fmtDur = (s) => { const t = Math.max(0, Math.round(s || 0)); return `${Math.floor(t / 60)}:${pad(t % 60)}`; };
/** The play control, the line, and the transcript when the AI layer has written one. */
export function voiceHtml(m, { showTranscript = true } = {}) {
  const md = (m && m.media) || {};
  const id = md.driveFileId || '';
  const tr = String((m && m.transcript) || '').trim();
  const dur = md.duration ? fmtDur(md.duration) : '';
  return `<div class="wa-voice" data-wa-voice="${escapeHtml(id)}">
      <button type="button" class="wa-play" data-wa-audio="${escapeHtml(id)}" data-wa-url="${escapeHtml(httpUrl(md.driveUrl))}" aria-label="Play voice note"${id ? '' : ' disabled'}>${PLAY_SVG}</button>
      <span class="wa-wave" aria-hidden="true"><span class="wa-wave-fill"></span></span>
      <span class="wa-tag">${escapeHtml(tr ? 'Transcribed' : (dur || 'Voice note'))}</span>
    </div>
    ${showTranscript ? (tr ? `<div class="wa-trans">${escapeHtml(tr)}</div>` : (m && m.ai && m.ai.pending) ? '<div class="wa-trans is-pending">Transcript pending.</div>' : '') : ''}`;
}

let actx = null;
const buffers = new Map();
let playing = null;   // { id, btn, src, startedAt, offset, raf, buffer }
function paintBtn(btn, on) {
  if (!btn) return;
  btn.innerHTML = on ? PAUSE_SVG : PLAY_SVG;
  btn.setAttribute('aria-label', on ? 'Pause voice note' : 'Play voice note');
  btn.classList.toggle('is-on', !!on);
}
function stopPlaying(keepOffset = false) {
  if (!playing) return;
  const p = playing;
  playing = null;
  cancelAnimationFrame(p.raf);
  try { p.src.onended = null; p.src.stop(); } catch (_) { /* already stopped */ }
  if (keepOffset) p.btn._waOffset = Math.min(p.buffer.duration, p.offset + (actx.currentTime - p.startedAt));
  paintBtn(p.btn, false);
}
async function toggleVoice(btn) {
  const id = btn.getAttribute('data-wa-audio');
  if (!id) return;
  if (playing && playing.btn === btn) { stopPlaying(true); return; }
  stopPlaying(true);
  const box = btn.closest('.wa-voice');
  const tag = box && box.querySelector('.wa-tag');
  try {
    actx = actx || new (window.AudioContext || window.webkitAudioContext)();
    if (actx.state === 'suspended') await actx.resume();
    let buffer = buffers.get(id);
    if (!buffer) {
      btn.classList.add('is-loading');
      if (tag) tag.dataset.was = tag.textContent;
      if (tag) tag.textContent = 'Loading…';
      const bytes = await downloadDriveFile(id);
      buffer = await actx.decodeAudioData(bytes);
      buffers.set(id, buffer);
      btn.classList.remove('is-loading');
      if (tag) tag.textContent = tag.dataset.was === 'Transcribed' ? 'Transcribed' : fmtDur(buffer.duration);
    }
    let offset = btn._waOffset || 0;
    if (offset >= buffer.duration - 0.05) offset = 0;
    const src = actx.createBufferSource();
    src.buffer = buffer;
    src.connect(actx.destination);
    src.start(0, offset);
    const fill = box && box.querySelector('.wa-wave-fill');
    playing = { id, btn, src, buffer, startedAt: actx.currentTime, offset, raf: 0 };
    const tick = () => {
      if (!playing || playing.btn !== btn) return;
      const t = playing.offset + (actx.currentTime - playing.startedAt);
      if (fill) fill.style.width = `${Math.min(100, (t / buffer.duration) * 100)}%`;
      playing.raf = requestAnimationFrame(tick);
    };
    tick();
    src.onended = () => { if (playing && playing.btn === btn) { stopPlaying(false); btn._waOffset = 0; if (fill) fill.style.width = '0%'; } };
    paintBtn(btn, true);
  } catch (e) {
    btn.classList.remove('is-loading');
    const url = httpUrl(btn.getAttribute('data-wa-url'));
    if (tag) {
      tag.innerHTML = `${escapeHtml('This browser cannot play it here.')}${url ? ` <a class="mq-link" href="${escapeHtml(safeUrl(url))}" target="_blank" rel="noopener">Open in Drive</a>` : ''}`;
    }
  }
}

/** One delegated listener per root: photos open, voice notes play. Idempotent. */
export function wireMedia(root) {
  hydrateThumbs(root);
  if (root.dataset.waWired) return;
  root.dataset.waWired = '1';
  root.addEventListener('click', (e) => {
    const play = e.target.closest('.wa-play');
    if (play && root.contains(play)) { e.preventDefault(); e.stopPropagation(); toggleVoice(play); return; }
    const ph = e.target.closest('.wa-ph');
    if (ph && root.contains(ph)) { e.preventDefault(); e.stopPropagation(); openPhoto(ph); }
  });
}
/** Stop a voice note when its screen goes (call from a teardown). */
export const stopVoice = () => stopPlaying(false);

// ── Lines under a message ──────────────────────────────────────────────────
/** "3 photos saved to 7241 › Site › 2026-10-17". */
export function savedLine(photos, projectNo, at) {
  const n = photos.length;
  if (!n || !projectNo) return '';
  return `${n === 1 ? 'Photo' : `${n} photos`} saved to ${projectNo} › ${SITE_FOLDER} › ${dayFolder(at)}`;
}

/** The record-as line: "Recorded as an instruction to confirm" (and a follow-up link where there is one). */
export function recordedHtml(m, { action = '' } = {}) {
  const r = m && m.recordedAs;
  if (!r || !r.kind) return '';
  return `<div class="wa-rec">${escapeHtml(recordedLine(r.kind))}${action ? ` · ${action}` : ''}</div>`;
}

/** Messages bunched as WhatsApp shows them: a text and the photos that followed it within 3 minutes, from the same person. */
export function bunch(messages) {
  const out = [];
  const GAP = 3 * 60e3;
  for (const m of messages) {
    const prev = out[out.length - 1];
    const kind = m.media ? mediaKind(m.media.mimeType || m.media.filename) : null;
    const t = (toDate(m.at) || new Date(0)).getTime();
    const sameSide = prev && prev.lead.threadId === m.threadId && prev.lead.direction === m.direction
      && senderKey(prev.lead) === senderKey(m) && t - prev.lastAt <= GAP;
    if (kind === 'image' && sameSide && prev.kind !== 'audio') {
      prev.photos.push(m); prev.items.push(m); prev.lastAt = t;
      if (!prev.caption && m.caption) prev.caption = m.caption;
      continue;
    }
    out.push({ lead: m, kind: kind === 'image' ? 'photos' : (kind || 'text'), photos: kind === 'image' ? [m] : [], items: [m], lastAt: t });
  }
  return out;
}
const senderKey = (m) => (m.direction === 'out' ? `o:${m.sentByUserId || m.sentByName || ''}` : `i:${(m.from && (m.from.waId || m.from.name)) || ''}:${(m.senderConfirmed && m.senderConfirmed.name) || ''}`);
